import { HtmlContent } from "@/components/Common/HtmlContent"
import { Tooltip } from "@/components/ui/tooltip"
import {
  Box,
  Button,
  EmptyState,
  Flex,
  Heading,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react"
import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableStateSnapshot,
  type DropResult,
  Droppable,
  type DroppableProvided,
} from "@hello-pangea/dnd"
import { memo, useEffect, useState } from "react"
import { FiEdit2, FiEye, FiPlus, FiTrash } from "react-icons/fi"
import { SectionCard } from "./SectionCard"
import { VirtualizedQuestionList } from "./VirtualizedQuestionList"
import type { QuizSection, SelectedQuestion } from "./quiz-builder-types"

const VIRTUALIZATION_THRESHOLD = 50 // Use virtualization for sections with 50+ questions

interface SectionedQuestionsPanelProps {
  sections: QuizSection[]
  unsectionedQuestions: SelectedQuestion[]
  onAddSection: () => void
  onRemoveSection: (sectionId: string) => void
  onUpdateSection: (sectionId: string, updates: { name?: string }) => void
  onToggleSection: (sectionId: string) => void
  onRemoveQuestion: (questionId: string, sectionId?: string) => void
  onClearSectionQuestions?: (sectionId: string) => void
  onPreviewQuestion: (questionId: string) => void
  onDragEnd: (result: DropResult) => void
}

export const SectionedQuestionsPanel = memo(function SectionedQuestionsPanel({
  sections,
  unsectionedQuestions,
  onAddSection,
  onRemoveSection,
  onUpdateSection,
  onToggleSection,
  onRemoveQuestion,
  onClearSectionQuestions,
  onPreviewQuestion,
  onDragEnd,
}: SectionedQuestionsPanelProps) {
  const totalQuestions = sections.reduce(
    (sum, section) => sum + section.questions.length,
    unsectionedQuestions.length,
  )

  const isEmpty = sections.length === 0 && unsectionedQuestions.length === 0

  // Compute the starting global index (1-based) for each section so that
  // questions across the entire quiz get a continuous numbering. Sections
  // appear first in the listed order, followed by unsectioned questions.
  const sectionStartIndices: number[] = []
  {
    let running = 0
    for (const section of sections) {
      sectionStartIndices.push(running)
      running += section.questions.length
    }
  }
  const unsectionedStartIndex = sections.reduce(
    (sum, section) => sum + section.questions.length,
    0,
  )

  return (
    <Box borderWidth="1px" borderRadius="md" p={4}>
      {/* Header */}
      <Flex align="center" justify="space-between" mb={4}>
        <Heading size="md">Selected questions</Heading>
        <Text fontSize="sm" color="fg.muted">
          {isEmpty
            ? "0 questions"
            : `${sections.length} ${
                sections.length === 1 ? "section" : "sections"
              }, ${totalQuestions} ${
                totalQuestions === 1 ? "question" : "questions"
              }`}
        </Text>
      </Flex>

      {/* Add Section Button */}
      <Button variant="outline" onClick={onAddSection} mb={4} gap={2} w="full">
        <FiPlus />
        Add Section
      </Button>

      {/* Empty State */}
      {isEmpty ? (
        <EmptyState.Root>
          <EmptyState.Content>
            <EmptyState.Indicator>
              <FiEye />
            </EmptyState.Indicator>
            <VStack textAlign="center">
              <EmptyState.Title>No questions selected</EmptyState.Title>
              <EmptyState.Description>
                Add questions from the bank to build your quiz
              </EmptyState.Description>
            </VStack>
          </EmptyState.Content>
        </EmptyState.Root>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <VStack align="stretch" gap={3}>
            {/* Sections */}
            <StrictModeDroppable droppableId="sections" type="SECTION">
              {(provided: DroppableProvided) => (
                <VStack
                  align="stretch"
                  gap={3}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {sections.map((section, index) => (
                    <Draggable
                      key={section.id}
                      draggableId={`section-${section.id}`}
                      index={index}
                    >
                      {(dragProvided: DraggableProvided) => (
                        <Box
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                        >
                          <SectionCard
                            section={section}
                            onToggleCollapse={onToggleSection}
                            onUpdateName={(sectionId, newName) =>
                              onUpdateSection(sectionId, { name: newName })
                            }
                            onDelete={onRemoveSection}
                            onClearQuestions={onClearSectionQuestions}
                            dragHandleProps={
                              dragProvided.dragHandleProps || undefined
                            }
                          >
                            {/* Questions within section - use virtualization for 50+ questions */}
                            {section.questions.length >=
                            VIRTUALIZATION_THRESHOLD ? (
                              <VirtualizedQuestionList
                                questions={section.questions}
                                sectionId={section.id}
                                droppableId={`section-${section.id}-questions`}
                                onRemoveQuestion={onRemoveQuestion}
                                onPreviewQuestion={onPreviewQuestion}
                                startIndex={sectionStartIndices[index] ?? 0}
                              />
                            ) : (
                              <Droppable
                                droppableId={`section-${section.id}-questions`}
                                type="QUESTION"
                              >
                                {(questionDropProvided: DroppableProvided) => (
                                  <VStack
                                    align="stretch"
                                    gap={2}
                                    ref={questionDropProvided.innerRef}
                                    {...questionDropProvided.droppableProps}
                                    minH="50px"
                                  >
                                    {section.questions.length === 0 ? (
                                      <Text
                                        fontSize="sm"
                                        color="fg.muted"
                                        textAlign="center"
                                        py={2}
                                      >
                                        No questions in this section
                                      </Text>
                                    ) : (
                                      section.questions.map(
                                        (item, questionIndex) => (
                                          <Draggable
                                            key={item.id}
                                            draggableId={`question-${item.id}`}
                                            index={questionIndex}
                                          >
                                            {(
                                              qDragProvided: DraggableProvided,
                                              qSnapshot: DraggableStateSnapshot,
                                            ) => (
                                              <Box
                                                ref={qDragProvided.innerRef}
                                                {...qDragProvided.draggableProps}
                                                {...qDragProvided.dragHandleProps}
                                                p={3}
                                                borderWidth="1px"
                                                borderRadius="md"
                                                bg={
                                                  qSnapshot.isDragging
                                                    ? "blue.50"
                                                    : "white"
                                                }
                                                _dark={{
                                                  bg: qSnapshot.isDragging
                                                    ? "blue.900"
                                                    : "gray.900",
                                                }}
                                              >
                                                <Flex
                                                  justify="space-between"
                                                  align="flex-start"
                                                  gap={3}
                                                >
                                                  <Box flex="1">
                                                    <Heading size="sm" mb={2}>
                                                      Question{" "}
                                                      {(sectionStartIndices[
                                                        index
                                                      ] ?? 0) +
                                                        questionIndex +
                                                        1}
                                                    </Heading>
                                                    <HtmlContent
                                                      lineClamp={3}
                                                      content={
                                                        item.data.question_text
                                                      }
                                                    />
                                                  </Box>
                                                  <Flex gap={2} wrap="wrap">
                                                    <Tooltip content="Preview question">
                                                      <IconButton
                                                        aria-label="Preview question"
                                                        variant="ghost"
                                                        size="sm"
                                                        colorPalette="gray"
                                                        onClick={() =>
                                                          onPreviewQuestion(
                                                            item.id,
                                                          )
                                                        }
                                                      >
                                                        <FiEye />
                                                      </IconButton>
                                                    </Tooltip>
                                                    <Tooltip content="Edit question">
                                                      <IconButton
                                                        asChild
                                                        aria-label="Edit question"
                                                        variant="ghost"
                                                        size="sm"
                                                        colorPalette="gray"
                                                      >
                                                        <a
                                                          href={`/questions/${item.id}/edit`}
                                                          target="_blank"
                                                          rel="noreferrer"
                                                        >
                                                          <FiEdit2 />
                                                        </a>
                                                      </IconButton>
                                                    </Tooltip>
                                                    <Tooltip content="Remove question">
                                                      <IconButton
                                                        aria-label="Remove question"
                                                        variant="ghost"
                                                        colorPalette="red"
                                                        onClick={() =>
                                                          onRemoveQuestion(
                                                            item.id,
                                                            section.id,
                                                          )
                                                        }
                                                        size="sm"
                                                      >
                                                        <FiTrash />
                                                      </IconButton>
                                                    </Tooltip>
                                                  </Flex>
                                                </Flex>
                                              </Box>
                                            )}
                                          </Draggable>
                                        ),
                                      )
                                    )}
                                    {questionDropProvided.placeholder}
                                  </VStack>
                                )}
                              </Droppable>
                            )}
                          </SectionCard>
                        </Box>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </VStack>
              )}
            </StrictModeDroppable>

            {/* Unsectioned Questions */}
            {unsectionedQuestions.length > 0 && (
              <SectionCard
                section={{
                  id: "unsectioned",
                  name: "Unsectioned",
                  order: 999,
                  questions: unsectionedQuestions,
                  isCollapsed: false,
                }}
                onToggleCollapse={onToggleSection}
                onUpdateName={(sectionId, newName) =>
                  onUpdateSection(sectionId, { name: newName })
                }
                onDelete={() => {}}
                onClearQuestions={onClearSectionQuestions}
                showDelete={false}
              >
                {/* Use virtualization for unsectioned questions if 50+ */}
                {unsectionedQuestions.length >= VIRTUALIZATION_THRESHOLD ? (
                  <VirtualizedQuestionList
                    questions={unsectionedQuestions}
                    sectionId="unsectioned"
                    droppableId="unsectioned-questions"
                    onRemoveQuestion={onRemoveQuestion}
                    onPreviewQuestion={onPreviewQuestion}
                    startIndex={unsectionedStartIndex}
                  />
                ) : (
                  <Droppable
                    droppableId="unsectioned-questions"
                    type="QUESTION"
                  >
                    {(questionDropProvided: DroppableProvided) => (
                      <VStack
                        align="stretch"
                        gap={2}
                        ref={questionDropProvided.innerRef}
                        {...questionDropProvided.droppableProps}
                      >
                        {unsectionedQuestions.map((item, index) => (
                          <Draggable
                            key={item.id}
                            draggableId={`question-${item.id}`}
                            index={index}
                          >
                            {(
                              qDragProvided: DraggableProvided,
                              qSnapshot: DraggableStateSnapshot,
                            ) => (
                              <Box
                                ref={qDragProvided.innerRef}
                                {...qDragProvided.draggableProps}
                                {...qDragProvided.dragHandleProps}
                                p={3}
                                borderWidth="1px"
                                borderRadius="md"
                                bg={qSnapshot.isDragging ? "blue.50" : "white"}
                                _dark={{
                                  bg: qSnapshot.isDragging
                                    ? "blue.900"
                                    : "gray.900",
                                }}
                              >
                                <Flex
                                  justify="space-between"
                                  align="flex-start"
                                  gap={3}
                                >
                                  <Box flex="1">
                                    <Heading size="sm" mb={2}>
                                      Question{" "}
                                      {unsectionedStartIndex + index + 1}
                                    </Heading>
                                    <HtmlContent
                                      lineClamp={3}
                                      content={item.data.question_text}
                                    />
                                  </Box>
                                  <Flex gap={2} wrap="wrap">
                                    <Tooltip content="Preview question">
                                      <IconButton
                                        aria-label="Preview question"
                                        variant="ghost"
                                        size="sm"
                                        colorPalette="gray"
                                        onClick={() =>
                                          onPreviewQuestion(item.id)
                                        }
                                      >
                                        <FiEye />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip content="Edit question">
                                      <IconButton
                                        asChild
                                        aria-label="Edit question"
                                        variant="ghost"
                                        size="sm"
                                        colorPalette="gray"
                                      >
                                        <a
                                          href={`/questions/${item.id}/edit`}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          <FiEdit2 />
                                        </a>
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip content="Remove question">
                                      <IconButton
                                        aria-label="Remove question"
                                        variant="ghost"
                                        colorPalette="red"
                                        onClick={() =>
                                          onRemoveQuestion(item.id)
                                        }
                                        size="sm"
                                      >
                                        <FiTrash />
                                      </IconButton>
                                    </Tooltip>
                                  </Flex>
                                </Flex>
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {questionDropProvided.placeholder}
                      </VStack>
                    )}
                  </Droppable>
                )}
              </SectionCard>
            )}
          </VStack>
        </DragDropContext>
      )}
    </Box>
  )
})

// StrictModeDroppable wrapper to fix React 18 StrictMode issue
type StrictModeDroppableProps = {
  children: (provided: DroppableProvided) => React.ReactNode
} & Omit<Parameters<typeof Droppable>[0], "children">

function StrictModeDroppable({
  children,
  ...droppableProps
}: StrictModeDroppableProps) {
  const isTestEnv = process.env.NODE_ENV === "test"
  const [enabled, setEnabled] = useState(isTestEnv)

  useEffect(() => {
    if (isTestEnv) {
      return
    }

    const animation = requestAnimationFrame(() => setEnabled(true))
    return () => cancelAnimationFrame(animation)
  }, [isTestEnv])

  if (!enabled) {
    return null
  }

  return (
    <Droppable {...droppableProps}>
      {(provided) => children(provided)}
    </Droppable>
  )
}
